import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Button from './Button';
import Input from './Input';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';

const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    emailNotifications: true,
    workshopReminders: true,
  });

  useEffect(() => {
    if (isOpen && user) {
      loadProfileData();
      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, user]);

  const loadProfileData = async () => {
    setIsLoading(true);
    try {
      // Load from profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('display_name, email_notifications, workshop_reminders')
        .eq('id', user.id)
        .single();

      if (!profileError && profile) {
        setProfileData({
          displayName: profile.display_name || '',
          email: user.email || '',
          emailNotifications: profile.email_notifications ?? true,
          workshopReminders: profile.workshop_reminders ?? true,
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile data' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Update profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          display_name: profileData.displayName,
          email_notifications: profileData.emailNotifications,
          workshop_reminders: profileData.workshopReminders,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' });

      if (profileError) {
        throw new Error(profileError.message);
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: 'Failed to save profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSave();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center pointer-events-auto"
      onClick={handleBackdropClick}
      style={{ isolation: 'isolate' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-none" aria-hidden="true" />

      {/* Modal */}
      <div
        className={cn(
          'relative bg-card border border-border rounded-lg shadow-xl',
          'w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden',
          'transition-all duration-200',
          'animate-in fade-in-0 zoom-in-95',
          'flex flex-col',
          'pointer-events-auto'
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Settings" size={20} color="var(--color-primary)" />
            </div>
            <h2 id="settings-title" className="text-2xl font-semibold text-foreground">
              Profile Settings
            </h2>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            disabled={isSaving}
            className="p-2 hover:bg-muted rounded-full transition-colors disabled:opacity-50"
            aria-label="Close settings"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Content */}
        <form id="profile-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Message */}
              {message.text && (
                <div
                  className={cn(
                    'p-4 rounded-lg flex items-center gap-3',
                    message.type === 'success' && 'bg-success/10 text-success border border-success/20',
                    message.type === 'error' && 'bg-error/10 text-error border border-error/20'
                  )}
                >
                  <Icon
                    name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                    size={20}
                  />
                  <span className="text-sm">{message.text}</span>
                </div>
              )}

              {/* Profile Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="User" size={18} color="var(--color-primary)" />
                  Profile Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="How should we call you?"
                    name="displayName"
                    value={profileData.displayName}
                    onChange={handleInputChange}
                    placeholder="Your preferred name"
                  />
                  
                  <Input
                    label="Email"
                    name="email"
                    value={profileData.email}
                    disabled
                    className="opacity-60"
                  />
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Bell" size={18} color="var(--color-primary)" />
                  Notification Preferences
                </h3>
                
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <Icon name="Mail" size={18} color="var(--color-muted-foreground)" />
                      <div>
                        <p className="font-medium text-foreground">Email Notifications</p>
                        <p className="text-sm text-muted-foreground">Receive important updates via email</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      name="emailNotifications"
                      checked={profileData.emailNotifications}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </label>

                  <label className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <Icon name="Calendar" size={18} color="var(--color-muted-foreground)" />
                      <div>
                        <p className="font-medium text-foreground">Workshop Reminders</p>
                        <p className="text-sm text-muted-foreground">Get notified about upcoming workshops</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      name="workshopReminders"
                      checked={profileData.workshopReminders}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button
            type="button"
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="profile-form"
            disabled={isSaving || isLoading}
            loading={isSaving}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettingsModal;
